"use client";

import React, { useRef, useState } from "react";
import { IoPlaySharp, IoPauseSharp } from "react-icons/io5";
import { motion, Variants } from "framer-motion";
import LocationDate from "./LocationDate";
import Image from "next/image";
import HeroImg from "@/assest/everest.jpg";
import RichText from "@/utils/richText";

interface HomeHeroProps {
  city?: { name: string; slug: string }[];
  videoSrc?: string;
  fallbackImage?: string;
  heroData?: HeroDataRecord[];
}

const textContainerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.3,
    },
  },
};

const textItemVariants: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  },
};

const HomeHero: React.FC<HomeHeroProps> = ({
  city,
  heroData,
  videoSrc = "/goodtravel.mp4",
  fallbackImage = "/abc.jpg",
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [paused, setPaused] = useState(false);
  const [videoError, setVideoError] = useState(false);

  const hero = heroData && heroData.length > 0 ? heroData[0] : null;
  const finalVideoSrc = hero?.video || videoSrc;

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (paused) {
      videoRef.current.play();
    } else {
      videoRef.current.pause();
    }
    setPaused(!paused);
  };

  return (
    <div className="relative w-full h-[100vh] md:h-[90vh] overflow-hidden bg-gray-900 mb-10">
      <motion.div
        initial={{ scale: 1.1 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.5, ease: "easeOut" }}
        className="absolute inset-0 w-full h-full"
      >
        {!videoError ? (
          <video
            ref={videoRef}
            src={finalVideoSrc}
            className="w-full h-full object-cover"
            onPlay={() => setPaused(false)}
            onPause={() => setPaused(true)}
            onError={() => setVideoError(true)}
            poster={fallbackImage}
            playsInline
            muted
            autoPlay
            loop
            preload="metadata"
          />
        ) : (
          <Image
            fill
            src={HeroImg}
            alt="Travel Hero"
            priority
            className="object-cover"
          />
        )}
      </motion.div>

      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/80 md:bg-gradient-to-tr md:from-black/80 md:via-black/20 md:to-transparent z-10" />

      <div className="relative z-20 h-full max-w-7xl mx-auto px-6 md:px-20 flex flex-col md:flex-row items-center justify-center md:justify-between pb-12 md:pb-20 gap-10">
        
        <motion.div
          variants={textContainerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-4 max-w-2xl text-center md:text-left"
        >
          <motion.h2
            variants={textItemVariants}
            className="text-white text-4xl sm:text-6xl uppercase text-center font-black"
          >
            {hero?.title}
          </motion.h2>
          <motion.div
            variants={textItemVariants}
            className="flex items-center justify-center gap-4"
          >
            <div className="hidden sm:block h-px w-12 md:w-20 bg-white/50" />
            <RichText
              content={hero?.subtitle || "Travel"}
              className="text-tertiary-500 text-sm font-medium italic tracking-[0.3em]"
            />
            <div className="hidden sm:block h-px w-12 md:w-20 bg-white/50" />
          </motion.div>
          
          <motion.div
            variants={textItemVariants}
            className="flex items-center justify-center gap-4"
          >
            <RichText
              content={hero?.description || "Travel"}
              className="text-white/80 text-sm font-normal text-center tracking-[0.2em]"
            />
          </motion.div>
        </motion.div>

        <div className="w-full sm:max-w-md lg:max-w-lg">
          <LocationDate city={city} />
        </div>
      </div>

      {!videoError && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute top-6 right-6 z-30"
        >
          <button
            onClick={togglePlay}
            type="button"
            className="group flex items-center justify-center w-12 h-12 rounded-full border border-white/20 bg-black/10 backdrop-blur-md transition-all hover:bg-white hover:border-white active:scale-90"
          >
            {paused ? (
              <IoPlaySharp size={20} className="text-white group-hover:text-black transition-colors" />
            ) : (
              <IoPauseSharp size={20} className="text-white group-hover:text-black transition-colors" />
            )}
          </button>
        </motion.div>
      )}
    </div>
  );
};

export default HomeHero;
